import React, { useId } from 'react';
import { getTpmsTireColor, TireStatus } from 'utils/tpms';
import BatteryIndicator from 'components/Tire/BatteryIndicator';

interface CombinedFourWheelsProps {
  pressureValues: Record<string, string | null>;
  temperatureValues: Record<string, string | null>;
  pressureStatusMap: Record<string, TireStatus>;
  temperatureStatusMap: Record<string, TireStatus>;
  batteryLow?: Record<string, boolean>;
  tireStaleMap?: Record<string, boolean>;
  width?: number | string;
  height?: number | string;
}

// viewBox: 0 0 100 150
// Tire: width=24, height=56, half=28
// Left col x=6, Right col x=70
// Row y positions: 1, 93

const TIRE_W = 24;
const TIRE_H = 56;
const TIRE_HALF = 28;
const LEFT_X = 6;
const RIGHT_X = 70;
const ROWS = [1, 93];
const AXLE_MIDS = [29, 121];

const tirePositions = [
  { id: 'p1', x: LEFT_X, y: ROWS[0], axis: 0 },
  { id: 'p2', x: RIGHT_X, y: ROWS[0], axis: 0 },
  { id: 'p3', x: LEFT_X, y: ROWS[1], axis: 1 },
  { id: 'p4', x: RIGHT_X, y: ROWS[1], axis: 1 },
];

function getTireTextColor(status: TireStatus, isStale?: boolean): string {
  if (
    isStale ??
    (status === TireStatus.NO_DATA || status === TireStatus.NO_SENSOR)
  ) {
    return '#2d3748';
  }
  return 'white';
}

const CombinedFourWheels: React.FC<CombinedFourWheelsProps> = ({
  pressureValues,
  temperatureValues,
  pressureStatusMap,
  temperatureStatusMap,
  batteryLow,
  tireStaleMap,
  width = 100,
  height = 150,
}) => {
  const uid = useId().replace(/:/g, '');

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 100 150"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {tirePositions.map(({ id, x, y }) => (
          <clipPath key={id} id={`clip-${uid}-${id}`}>
            <rect x={x} y={y} width={TIRE_W} height={TIRE_H} rx={3} />
          </clipPath>
        ))}
      </defs>

      {/* Front axle */}
      <line
        x1={LEFT_X + TIRE_W}
        y1={AXLE_MIDS[0]}
        x2={RIGHT_X}
        y2={AXLE_MIDS[0]}
        stroke="#4A5C72"
        strokeWidth="5"
      />
      {/* Rear axle */}
      <line
        x1={LEFT_X + TIRE_W}
        y1={AXLE_MIDS[1]}
        x2={RIGHT_X}
        y2={AXLE_MIDS[1]}
        stroke="#4A5C72"
        strokeWidth="5"
      />
      {/* Center spine */}
      <line
        x1={50}
        y1={AXLE_MIDS[0]}
        x2={50}
        y2={AXLE_MIDS[1]}
        stroke="#4A5C72"
        strokeWidth="5"
      />

      {tirePositions.map(({ id, x, y }) => {
        const pKey = id;
        const isStale = tireStaleMap?.[pKey];
        const pressureStatus = pressureStatusMap[pKey] ?? TireStatus.NO_SENSOR;
        const tempStatus = temperatureStatusMap[pKey] ?? TireStatus.NO_SENSOR;
        const pressureColor = getTpmsTireColor(pressureStatus, isStale);
        const tempColor = getTpmsTireColor(tempStatus, isStale);
        const pVal = pressureValues[pKey] ?? '--';
        const tVal = temperatureValues[pKey] ?? '--';
        const cx = x + TIRE_W / 2;
        const pyCenter = y + TIRE_HALF / 2;
        const tyCenter = y + TIRE_HALF + TIRE_HALF / 2;
        const pTextColor = getTireTextColor(pressureStatus, isStale);
        const tTextColor = getTireTextColor(tempStatus, isStale);

        return (
          <g key={id}>
            <g clipPath={`url(#clip-${uid}-${id})`}>
              <rect
                x={x}
                y={y}
                width={TIRE_W}
                height={TIRE_HALF}
                fill={pressureColor}
              />
              <rect
                x={x}
                y={y + TIRE_HALF}
                width={TIRE_W}
                height={TIRE_HALF}
                fill={tempColor}
              />
            </g>
            {/* Divider line */}
            <line
              x1={x}
              y1={y + TIRE_HALF}
              x2={x + TIRE_W}
              y2={y + TIRE_HALF}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="0.5"
            />
            <text
              x={cx}
              y={pyCenter}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={pTextColor}
              fontSize={11}
              fontWeight={600}
              style={{ pointerEvents: 'none' }}
              transform={`rotate(-90, ${cx}, ${pyCenter})`}
            >
              {pVal}
            </text>
            <text
              x={cx}
              y={tyCenter}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={tTextColor}
              fontSize={11}
              fontWeight={600}
              style={{ pointerEvents: 'none' }}
              transform={`rotate(-90, ${cx}, ${tyCenter})`}
            >
              {tVal}
            </text>
            <BatteryIndicator
              cx={cx}
              cy={y}
              tireBottomY={y + TIRE_H}
              tireWidth={TIRE_W}
              show={batteryLow?.[pKey]}
            />
          </g>
        );
      })}
    </svg>
  );
};

export default CombinedFourWheels;
